/**
 * 표의 정렬·거르기 — 화면·플레이리스트·캠페인 목록이 같이 쓴다.
 *
 * 받아 온 줄을 브라우저에서 다시 늘어놓을 뿐이다. 서버는 목록마다 정한 순서로 주고
 * (화면은 이름, 플레이리스트는 최근 수정, 캠페인은 우선순위), 여기서는 **같은 값끼리 그
 * 순서를 지킨다** — 우선순위가 같은 캠페인 둘이 정렬할 때마다 자리를 바꾸면, 엔진이 고르는
 * 쪽(먼저 만든 것)이 어느 것인지 표에서 읽을 수 없게 된다.
 */

export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/**
 * 정렬 값. **null 은 "모름"** 이고 방향과 상관없이 맨 뒤에 둔다.
 *
 * 남은 공간을 적은 순으로 보는 사람은 바닥난 기기를 찾는 중이다. 보고하지 않는 기기를
 * 0 처럼 맨 앞에 세우면 찾는 것이 그 아래로 밀리고, 뒤집어 많은 순으로 봐도 맨 앞을 차지한다.
 */
export type SortValue = string | number | null;

/** 한글 이름을 가나다로, 숫자는 값으로("화면 2" 가 "화면 10" 앞). */
const collator = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' });

export function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'string' && typeof b === 'string') return collator.compare(a, b);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  // 한 열에 글자와 숫자가 섞이는 일은 없지만, 섞여도 순서가 흔들리지 않게 숫자를 앞에.
  return typeof a === 'number' ? -1 : 1;
}

export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K>,
  valueOf: (row: T, key: K) => SortValue,
): T[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: valueOf(row, sort.key) }))
    .sort((x, y) => {
      if (x.value === null || y.value === null) {
        if (x.value === y.value) return x.index - y.index;
        return x.value === null ? 1 : -1;
      }
      return sign * compareValues(x.value, y.value) || x.index - y.index;
    })
    .map((entry) => entry.row);
}

/** 머리를 눌렀을 때 — 같은 열이면 방향만 뒤집고, 다른 열이면 그 열이 먼저 보일 방향으로. */
export function nextSort<K extends string>(current: SortState<K>, key: K, firstDir: SortDir): SortState<K> {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: firstDir };
}

/**
 * 열마다 고른 값들. **빈 목록은 거르지 않는다**(전체).
 *
 * "보일 것을 고른다" 로 둔다. 엑셀처럼 "숨길 것을 끈다" 로 두면 그사이 새로 생긴 값(새 버전을
 * 받은 화면, 새 플레이리스트)이 고른 적도 없이 숨겨져, 목록이 조용히 줄어든다.
 */
export type Filters<K extends string> = Partial<Record<K, readonly string[]>>;

export function applyFilters<T, K extends string>(
  rows: readonly T[],
  filters: Filters<K>,
  valueOf: (row: T, key: K) => string,
): T[] {
  const active = (Object.entries(filters) as Array<[K, readonly string[] | undefined]>).filter(
    (entry): entry is [K, readonly string[]] => (entry[1]?.length ?? 0) > 0,
  );
  if (active.length === 0) return [...rows];
  return rows.filter((row) => active.every(([key, chosen]) => chosen.includes(valueOf(row, key))));
}

export function activeFilterCount<K extends string>(filters: Filters<K>): number {
  return Object.values<readonly string[] | undefined>(filters).filter((chosen) => (chosen?.length ?? 0) > 0).length;
}

export interface FilterOption {
  value: string;
  label: string;
  /** 이름 옆에 흐리게 붙는 말 — 같은 이름의 플레이리스트와 스케줄을 가른다. */
  hint?: string;
  count: number;
}

/**
 * 거르개에 내놓을 값 — 받아 온 줄에 있는 값과 그 수.
 *
 * 골라 둔 값이 목록에서 사라져도(그 버전의 화면이 모두 올라갔다) 0 으로 남긴다. 빼 버리면
 * 걸려 있는 조건을 풀 자리가 없어, 빈 표 앞에서 이유를 찾지 못한다.
 */
export function filterOptions<T>(
  rows: readonly T[],
  valueOf: (row: T) => string,
  describe: (value: string) => { label: string; hint?: string },
  chosen: readonly string[],
  order?: (a: FilterOption, b: FilterOption) => number,
): FilterOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value = valueOf(row);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  for (const value of chosen) if (!counts.has(value)) counts.set(value, 0);
  const options = [...counts].map(([value, count]) => ({ value, count, ...describe(value) }));
  return options.sort(order ?? ((a, b) => compareValues(a.label, b.label)));
}

/** 고정된 값 목록(상태 같은)을 그 순서대로 늘어놓는 비교. 목록에 없는 값은 뒤로. */
export function fixedOrder(values: readonly string[]): (a: FilterOption, b: FilterOption) => number {
  const rank = (value: string): number => {
    const index = values.indexOf(value);
    return index === -1 ? values.length : index;
  };
  return (a, b) => rank(a.value) - rank(b.value) || compareValues(a.label, b.label);
}

/** "0.7.10 (shell 0.7.10)" → 정렬할 수 있는 수. 모르면 null. 자리마다 999 까지(0.7.10 > 0.7.9). */
export function versionRank(appVersion: string | null | undefined): number | null {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(appVersion?.trim() ?? '');
  if (!match) return null;
  return Number(match[1]) * 1_000_000 + Number(match[2]) * 1_000 + Number(match[3]);
}

/** "0.7.10 (shell 0.7.10)" → "0.7.10". 모르면 빈 문자열 — 거르개의 "모름" 값이다. */
export function versionOf(appVersion: string | null | undefined): string {
  return /^(\d+\.\d+\.\d+)/.exec(appVersion?.trim() ?? '')?.[1] ?? '';
}

/**
 * 정렬은 기억한다 — 남은 공간 순으로 보는 사람은 들어올 때마다 그 순서로 본다.
 * 거르기는 기억하지 않는다. 걸린 줄 모르는 거르개는 "화면이 사라졌다" 로 읽힌다.
 */
export function readSort<K extends string>(
  table: string,
  keys: readonly K[],
  fallback: SortState<K>,
): SortState<K> {
  try {
    const raw = window.localStorage.getItem(`signageone.sort.${table}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<SortState<K>>;
    if (parsed.key && keys.includes(parsed.key) && (parsed.dir === 'asc' || parsed.dir === 'desc')) {
      return { key: parsed.key, dir: parsed.dir };
    }
  } catch {
    // 저장소를 쓸 수 없는 브라우저(사생활 보호 창 등)에서는 기본 순서로.
  }
  return fallback;
}

export function writeSort<K extends string>(table: string, sort: SortState<K>): void {
  try {
    window.localStorage.setItem(`signageone.sort.${table}`, JSON.stringify(sort));
  } catch {
    // 기억하지 못할 뿐 정렬은 된다.
  }
}
